import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Mail, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

// Drill-down list for a funnel segment: numbered rows + optional note + CTA.
// When `data.onReinstate` is given the rows become selectable and a
// "Reinstate selected" action opens the reinstate flow for those companies.
export const FunnelListDialog = ({ data, onClose }) => {
  const [sel, setSel] = useState(new Set());
  useEffect(() => { setSel(new Set()); }, [data]);
  const selectable = !!data?.onReinstate;
  const toggle = (name) => setSel(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const allOn = data && sel.size === data.rows.length && data.rows.length > 0;
  return (
    <Dialog open={!!data} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="funnel-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{data?.title}</DialogTitle>
          {data?.hint && <p className="text-xs text-slate-500">{data.hint}</p>}
        </DialogHeader>
        {data && (
          <>
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="funnel-dialog-copy"
                  onClick={() => { navigator.clipboard.writeText(data.rows.map(r => r.name).join('\n')); toast.success(`Copied ${data.rows.length} names`); }}>
                  <Copy className="w-3 h-3 mr-1" /> Copy names
                </Button>
                {selectable && data.rows.length > 0 && (
                  <button type="button" className="text-[11px] text-sky-700 hover:underline" data-testid="funnel-dialog-select-all"
                    onClick={() => setSel(allOn ? new Set() : new Set(data.rows.map(r => r.name)))}>{allOn ? 'Clear selection' : 'Select all'}</button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectable && (
                  <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!sel.size} data-testid="funnel-dialog-reinstate"
                    onClick={() => data.onReinstate(data.rows.filter(r => sel.has(r.name)))}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Reinstate selected{sel.size ? ` (${sel.size})` : ''}
                  </Button>
                )}
                {data.cta && (
                  <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={data.cta.onClick} data-testid="funnel-dialog-cta">
                    <Mail className="w-3 h-3 mr-1" /> {data.cta.label}
                  </Button>
                )}
              </div>
            </div>
            {data.rows.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">Nothing here.</p>
            ) : (
              <ol className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                {data.rows.map((r, i) => (
                  <li key={r.name} className={`flex items-start gap-3 px-3 py-1.5 text-xs ${selectable ? 'cursor-pointer hover:bg-slate-50' : ''}`} data-testid={`funnel-row-${r.name}`}
                    onClick={selectable ? () => toggle(r.name) : undefined}>
                    {selectable && <input type="checkbox" checked={sel.has(r.name)} onChange={() => toggle(r.name)} onClick={e => e.stopPropagation()} className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-emerald-600" data-testid={`funnel-row-check-${r.name}`} />}
                    <span className="w-6 text-right tabular-nums text-slate-400 shrink-0">{i + 1}.</span>
                    <span className="font-medium text-slate-800 min-w-0 flex-1">{r.name}</span>
                    {r.note && <span className="text-slate-500 text-right max-w-[50%]">{r.note}</span>}
                    {selectable && (
                      <button type="button" className="text-[10px] text-emerald-700 hover:underline shrink-0" data-testid={`funnel-row-reinstate-${r.name}`}
                        onClick={(e) => { e.stopPropagation(); data.onReinstate([r]); }}>Reinstate</button>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
